import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { Layout, message, Modal } from 'antd'
import { CloseOutlined, MenuOutlined } from '@ant-design/icons'

import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import ApiErrorHandlerProvider from './components/ApiErrorHandler/Provider'
import Breadcrumb from './components/Layout/Navigation/Breadcrumb'
import MenuDrawer from './components/Layout/Navigation/Menu/Drawer'
import Sidebar from './components/Layout/Navigation/Sidebar'
import { useSanitizeHtml } from './pages/novidades/hooks'
import useNovidades from './pages/novidades/hooks/useNovidades'

import './App.less'

const { Content, Header } = Layout

const NOVIDADE_TYPES = {
  AVISO_IMPORTANTE: 2,
  NOVIDADE_COMUM: 1,
}

const MODAL_TEXTS = {
  AVISO_TITLE: 'Novo Aviso Importante',
  NOVIDADE_TITLE: 'Novas Novidades',
  NOVIDADE_CONTENT: 'Há novidades disponíveis para visualização!',
  OK_TEXT: 'Ver Novidades',
}

const STORAGE_KEY = 'lastNovidadeHash'

const LocalStorageUtils = {
  getStoredHash: () => {
    try {
      return localStorage?.getItem(STORAGE_KEY) || null
    } catch (error) {
      console.warn('Erro ao acessar localStorage:', error)
      return null
    }
  },

  setStoredHash: (hash) => {
    try {
      localStorage?.setItem(STORAGE_KEY, hash)
    } catch (error) {
      console.warn('Erro ao salvar no localStorage:', error)
    }
  },
}

const handleGlobalError = (error) => {
  if (!(error instanceof AxiosError)) {
    message.error('Ocorreu um erro inesperado.')
    return
  }

  const status = error.response?.status
  const errorMessage = error.response?.data?.message || error.message

  const errorMap = {
    403: 'Você não tem permissão para realizar esta operação.',
    404: 'O recurso solicitado não foi encontrado.',
    500: 'Erro interno no servidor. Tente novamente mais tarde.',
  }

  message.error(errorMap[status] || `Erro: ${errorMessage}`)
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60 * 5,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      handleGlobalError(error)
    },
  }),
})

const App = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true)
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false)
  const sidebarRef = useRef(null)
  const [activeSubmenu, setActiveSubmenu] = useState(null)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const navigate = useNavigate()
  const { novidadesRequest } = useNovidades()
  const [lastNovidadeHash, setLastNovidadeHash] = useState(() => LocalStorageUtils.getStoredHash())

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)

      if (mobile) {
        setSidebarCollapsed(true)
      } else {
        setMobileMenuVisible(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const generateNovidadeHash = useCallback((novidade) => {
    const content = `${novidade.assunto}-${novidade.mensagem}-${novidade.tipo}`

    const encoder = new TextEncoder()
    const bytes = encoder.encode(content)

    const latin1String = String.fromCharCode(...bytes)

    return btoa(latin1String)
      .replace(/[^a-zA-Z0-9]/g, '')
      .substring(0, 16)
  }, [])

  const novidadesWithUuid = useMemo(() => {
    if (!novidadesRequest.data?.results?.length) return []

    return novidadesRequest.data.results.map((novidade) => ({
      ...novidade,
      contentHash: generateNovidadeHash(novidade),
    }))
  }, [novidadesRequest.data?.results, generateNovidadeHash])

  const primeiroItem = novidadesWithUuid[0]
  const mensagemParaSanitizar = primeiroItem?.tipo === NOVIDADE_TYPES.AVISO_IMPORTANTE ? primeiroItem.mensagem : ''
  const mensagemSanitizada = useSanitizeHtml(mensagemParaSanitizar)

  const showAvisoImportanteModal = useCallback(() => {
    Modal.warning({
      title: MODAL_TEXTS.AVISO_TITLE,
      content: (
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: mensagemSanitizada }}
        />
      ),
      width: 540,
    })
  }, [mensagemSanitizada])

  const showNovidadeModal = useCallback(() => {
    Modal.info({
      title: MODAL_TEXTS.NOVIDADE_TITLE,
      content: MODAL_TEXTS.NOVIDADE_CONTENT,
      okText: MODAL_TEXTS.OK_TEXT,
      onOk: () => navigate('/novidade'),
    })
  }, [navigate])

  const checkNovasNovidades = useCallback(() => {
    if (novidadesWithUuid.length === 0) return

    if (lastNovidadeHash === primeiroItem.contentHash) {
      return
    }

    if (primeiroItem.tipo === NOVIDADE_TYPES.AVISO_IMPORTANTE) {
      showAvisoImportanteModal(primeiroItem.mensagem)
    } else {
      showNovidadeModal()
    }

    setLastNovidadeHash(primeiroItem.contentHash)
    LocalStorageUtils.setStoredHash(primeiroItem.contentHash)
  }, [
    novidadesWithUuid,
    lastNovidadeHash,
    showAvisoImportanteModal,
    showNovidadeModal,
    LocalStorageUtils.setStoredHash,
  ])

  const handleClickOutside = useCallback(
    (event) => {
      const sidebarElement = sidebarRef.current
      const submenuPanelElement = document.querySelector('.app__submenu-panel')

      const isClickOutsideSidebar = sidebarElement && !sidebarElement.contains(event.target)
      const isClickOutsideSubmenu = !submenuPanelElement || !submenuPanelElement.contains(event.target)

      if (!sidebarCollapsed && isClickOutsideSidebar && isClickOutsideSubmenu) {
        setSidebarCollapsed(true)
      }

      if (mobileMenuVisible) setMobileMenuVisible(false)

      if (activeSubmenu && isClickOutsideSidebar && isClickOutsideSubmenu) {
        setActiveSubmenu(null)
      }
    },
    [sidebarCollapsed, activeSubmenu],
  )

  useEffect(() => {
    if (novidadesWithUuid.length > 0) {
      checkNovasNovidades()
    }
  }, [checkNovasNovidades, novidadesWithUuid])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [handleClickOutside])

  const handleOverlayKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      setMobileMenuVisible(false)
    }
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Layout className="app__layout">
        <Sidebar
          ref={sidebarRef}
          collapsed={isMobile ? true : sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
          activeSubmenu={activeSubmenu}
          setActiveSubmenu={setActiveSubmenu}
          novidadesData={{
            ...novidadesRequest.data,
            results: novidadesWithUuid,
          }}
          mobileMenuVisible={mobileMenuVisible}
        />

        <Layout className="app__main">
          <Header>
            {isMobile && (
              <button
                type="button"
                className={`menu-trigger ${mobileMenuVisible ? 'open' : ''}`}
                onClick={() => setMobileMenuVisible(!mobileMenuVisible)}
                aria-label="Abrir menu"
              >
                {mobileMenuVisible ? <CloseOutlined /> : <MenuOutlined />}
              </button>
            )}

            <MenuDrawer
              onMenuClicked={() => {
                if (isMobile) {
                  setMobileMenuVisible(!mobileMenuVisible)
                } else {
                  setSidebarCollapsed(!sidebarCollapsed)
                }
              }}
              menuIcon={mobileMenuVisible || !sidebarCollapsed ? <CloseOutlined /> : <MenuOutlined />}
            />
          </Header>

          {mobileMenuVisible && (
            <div
              className="app__sidebar-overlay visible"
              onClick={() => setMobileMenuVisible(false)}
              onKeyDown={handleOverlayKeyDown}
              role="button"
              tabIndex={0}
              aria-label="Fechar menu"
            />
          )}

          <Content className="app__content">
            <Breadcrumb />
            <ApiErrorHandlerProvider>
              <Outlet
                context={{
                  novidadesRequest: {
                    ...novidadesRequest,
                    data: {
                      ...novidadesRequest.data,
                      results: novidadesWithUuid,
                    },
                  },
                }}
              />
            </ApiErrorHandlerProvider>
          </Content>
        </Layout>
      </Layout>
    </QueryClientProvider>
  )
}

export default App
